import React, { FC, useState } from "react";
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Paper,
  TableContainer,
  Checkbox,
  IconButton,
  Box,
  Button,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { TableRow as MuiTableRow } from "@mui/material";
import { GetInsuranceTransactionDto } from "../../../../../api-client";
import { BillingStatus } from "../pages/InsurancePageLayout";
import { formatDateUI } from "../../../../../helpers/dateFormat";
import { useDownload } from "../../../../../hooks/useDownload";

import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import CachedIcon from "@mui/icons-material/Cached";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { PdfRegenerationPopup } from "./PdfRegenerationPopup";
import { BillFullScreenDialog } from "../dialogs/BillFullScreenDialog";
import { ExportModalV2 } from "./ExportModal/v2/ExportModalV2";
import ReceiptIcon from "@mui/icons-material/Receipt";

interface InsuranceBillsTableProps {
  bills: GetInsuranceTransactionDto[];
  billStatus: BillingStatus;
  selectedBillIds: string[];
  onSelectAll: (checked: boolean) => void;
  onSelectOne: (id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string) => void;
  onRegeneratePdfs?: (billId: string, date: string) => Promise<void>;
}

export const InsuranceBillsTable: FC<InsuranceBillsTableProps> = ({
  bills,
  billStatus,
  selectedBillIds,
  onSelectAll,
  onSelectOne,
  onEdit,
  onDelete,
  onStatusChange,
  onRegeneratePdfs,
}) => {
  const sortedBills = bills.sort((a, b) => {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
  const allChecked = sortedBills.length > 0 && selectedBillIds.length === sortedBills.length;
  const indeterminate = selectedBillIds.length > 0 && selectedBillIds.length < sortedBills.length;
  const { handleDownloadPdf } = useDownload();

  // PDF Regeneration popup state
  const [isRegeneratePopupOpen, setIsRegeneratePopupOpen] = useState(false);
  const [selectedBillForRegeneration, setSelectedBillForRegeneration] = useState<string | null>(
    null,
  );
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Bill Full Screen Dialog state
  const [isBillDialogOpen, setIsBillDialogOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<GetInsuranceTransactionDto | null>(
    null,
  );

  // PDF Regeneration handlers
  const handleRegenerateClick = (billId: string) => {
    setSelectedBillForRegeneration(billId);
    setIsRegeneratePopupOpen(true);
  };

  const handleRegenerateClose = () => {
    setIsRegeneratePopupOpen(false);
    setSelectedBillForRegeneration(null);
  };

  const handleRegenerateConfirm = async (date: string) => {
    if (!selectedBillForRegeneration || !onRegeneratePdfs) return;

    try {
      setIsRegenerating(true);
      await onRegeneratePdfs(selectedBillForRegeneration, date);
      handleRegenerateClose();
    } catch (error) {
      console.error("Error regenerating PDFs:", error);
    } finally {
      setIsRegenerating(false);
    }
  };

  // Bill Full Screen Dialog handlers
  const handleViewBillClick = (bill: GetInsuranceTransactionDto) => {
    setSelectedTransaction(bill);
    setIsBillDialogOpen(true);
  };

  const handleBillDialogClose = () => {
    setIsBillDialogOpen(false);
    setSelectedTransaction(null);
  };

  return (
    <TableContainer component={Paper} className="scrollable" sx={{ maxHeight: "550px" }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" className="table-header-cell">
              {billStatus === BillingStatus.START && (
                <Checkbox
                  indeterminate={indeterminate}
                  checked={allChecked}
                  onChange={(e) => onSelectAll(e.target.checked)}
                />
              )}
            </TableCell>
            <TableCell className="table-header-cell">Rechnung Nummer</TableCell>
            <TableCell className="table-header-cell">Kostenträger</TableCell>
            <TableCell className="table-header-cell">Gesamtpreis (€)</TableCell>
            <TableCell className="table-header-cell">Erstellungsdatum</TableCell>
            <TableCell className="table-header-cell">Erstellt von</TableCell>
            <TableCell className="table-header-cell">Mandant</TableCell>
            {billStatus === BillingStatus.START && (
              <TableCell className="table-header-cell">Aktionen</TableCell>
            )}
            {(billStatus === BillingStatus.OPEN || billStatus === BillingStatus.PREVIOUS) && (
              <TableCell className="table-header-cell">Rechnung</TableCell>
            )}
            {(billStatus === BillingStatus.OPEN || billStatus === BillingStatus.PREVIOUS) && (
              <TableCell className="table-header-cell">Aktionen</TableCell>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {sortedBills.map((row) => {
            const isChecked = selectedBillIds.includes(row.id);
            // console.log(" LETS SEE THE SORTED ROW", row.id);
            return (
              <MuiTableRow
                role="checkbox"
                aria-checked={isChecked}
                tabIndex={-1}
                key={row.id}
                selected={isChecked}
                className="table-row-insurance"
                sx={{
                  "&:not(:last-child)": { borderBottom: "1px solid #e0e0e0" },
                  height: "50px",
                }}
              >
                <TableCell padding="checkbox">
                  {billStatus === BillingStatus.START && (
                    <Checkbox checked={isChecked} onChange={() => onSelectOne(row.id)} />
                  )}
                </TableCell>
                <TableCell component="th" scope="row" sx={{ fontSize: 15 }}>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {row.transactionNumber}
                    {row.isDelayed && (
                      <Tooltip
                        title={`Verzögert: ${Number(row.amountDelayed || 0)
                          .toFixed(2)
                          .replace(".", ",")} €`}
                      >
                        <PauseCircleOutlineIcon sx={{ fontSize: 18, color: "#d32f2f" }} />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 15 }}>
                  {row.payer.name}
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 15 }}>
                  {Number(row.amount).toFixed(2).replace(".", ",") + " €"}
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 15 }}>
                  {formatDateUI(row.createdAt)}
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 15 }}>
                  {row.user.firstName + " " + row.user.lastName}
                </TableCell>
                <TableCell align="left" sx={{ fontSize: 15 }}>
                  {row.account.name}
                </TableCell>
                {billStatus === BillingStatus.START && (
                  <TableCell align="left">
                    <IconButton className="fancy-button" onClick={() => onEdit(row.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => onDelete(row.id)} aria-label="delete">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
                {(billStatus === BillingStatus.OPEN || billStatus === BillingStatus.PREVIOUS) && (
                  <TableCell align="left">
                    {(row.billPdf?.id || row.paperCenterReceipt?.id || row.docDto?.id) && (
                      <Box display="flex" gap={1}>
                        {row.paperCenterReceipt?.id && (
                          <IconButton
                            className="fancy-button"
                            onClick={() => handleDownloadPdf(row.paperCenterReceipt!.id)}
                          >
                            <HistoryEduIcon />
                          </IconButton>
                        )}
                        {row.billPdf?.id && (
                          <IconButton
                            className="fancy-button"
                            onClick={() => handleDownloadPdf(row.billPdf!.id)}
                          >
                            <RequestQuoteIcon />
                          </IconButton>
                        )}
                        {row.docDto?.id && (
                          <IconButton
                            className="fancy-button"
                            onClick={() => handleDownloadPdf(row.docDto!.id)}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        )}

                        <Box
                          sx={{
                            elevation: 4,
                            borderRadius: 1,
                            backgroundColor: "#f5f5f5",
                            padding: 0.1,
                            border: "1px solid #767676ff",
                            "&:hover": {
                              backgroundColor: "#b0acacff",
                              elevation: 6,
                            },
                          }}
                        >
                          <Tooltip title="PDFs neu generieren">
                            <IconButton
                              onClick={() => handleRegenerateClick(row.id)}
                              disabled={!onRegeneratePdfs}
                              className="fancy-button"
                              sx={{
                                "&:hover": {
                                  backgroundColor: "transparent",
                                },
                              }}
                            >
                              <CachedIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    )}
                  </TableCell>
                )}
                {(billStatus === BillingStatus.OPEN || billStatus === BillingStatus.PREVIOUS) && (
                  <TableCell align="left">
                    <Box display="flex" gap={1} alignItems="center">
                      <IconButton onClick={() => handleViewBillClick(row)}>
                        <VisibilityIcon />
                      </IconButton>
                      {billStatus === BillingStatus.OPEN && (
                        <Button
                          size="small"
                          sx={{ color: "#fff", backgroundColor: "#3f51b5" }}
                          variant="contained"
                          onClick={() => onStatusChange(row.id)}
                        >
                          ausbuchen
                        </Button>
                      )}
                    </Box>
                  </TableCell>
                )}
              </MuiTableRow>
            );
          })}
        </TableBody>
      </Table>

      <PdfRegenerationPopup
        open={isRegeneratePopupOpen}
        onClose={handleRegenerateClose}
        onConfirm={handleRegenerateConfirm}
        isLoading={isRegenerating}
      />

      <BillFullScreenDialog
        open={isBillDialogOpen}
        titleComp={<span>Abrechnung (Exportiert)</span>}
        actions={
          <Button variant="text" onClick={() => handleBillDialogClose()}>
            Schließen
          </Button>
        }
      >
        {selectedTransaction && (
          <ExportModalV2 transaction={selectedTransaction} onClose={handleBillDialogClose} />
        )}
      </BillFullScreenDialog>
    </TableContainer>
  );
};
